import { useCallback, useEffect, useRef, useState } from 'react'
import {
  formatProductCountdownText,
  PRODUCT_COUNTDOWN_INITIAL_TENTHS,
  PRODUCT_COUNTDOWN_INTERVAL_MS,
} from '../../components/application-flow-product-retention-dialog/product-countdown'
import { AuthorizeSkinViewModel } from '../../types'

const RETENTION_TIMER_DELAY = 10000
const RETENTION_CLOSE_DELAY = 1500

interface Options {

  /** 旧容器投影的协议与产品选择状态；本 Hook 只读取以决定本地弹窗展示。 */
  model: AuthorizeSkinViewModel

  /** 用户点击返回时委托旧容器执行既有返回逻辑。 */
  onBack: () => void

  /** 旧容器保留的授权提交动作。 */
  onSubmit: () => void

  /** 用户同意皮肤本地协议弹窗后，旧容器更新协议状态并沿用原提交链路。 */
  onConfirmAgreement: () => void
}

export interface AuthorizeSkinController {

  /** 是否显示皮肤本地协议确认弹窗，不参与旧业务状态。 */
  agreementDialogVisible: boolean

  /** 是否显示挽留弹窗，不参与旧业务状态。 */
  retentionVisible: boolean

  /** 挽留弹窗关闭按钮是否允许点击。 */
  retentionCanClose: boolean

  /** 首段领取倒计时结束后展示的领取按钮文案。 */
  submitLabel: string

  /** 与底部领取按钮共用的额度失效倒计时文案。 */
  productCountdownText: string

  /** 拦截顶部返回或系统返回；优先触发挽留弹窗，已触发过则放行原返回动作。 */
  onBack: () => void

  /** 用户点击提交时先执行产品选择与协议确认的展示分流。 */
  onSubmit: () => void

  /** 用户确认同意协议后交回旧容器处理。 */
  onConfirmAgreement: () => void

  /** 用户关闭协议确认弹窗时只改变本地展示状态。 */
  onCloseAgreementDialog: () => void

  /** 用户关闭挽留弹窗时只改变本地展示状态。 */
  onCloseRetention: () => void

  /** 用户在挽留弹窗中点击提交时关闭弹窗，并按弹窗文案视为已同意协议后提交。 */
  onSubmitFromRetention: () => void
}

/**
 * useAuthorizeSkinController — 管理授权皮肤的本地协议确认弹窗与挽留弹窗。
 * 产品校验、协议状态、接口请求和跳转均保留在旧授权业务容器；本 Hook 只管理本地协议确认与挽留弹窗展收。
 * @returns {AuthorizeSkinController} 仅供授权皮肤 Container 与 View 使用的本地交互接口
 */
const useAuthorizeSkinController = ({
  model,
  onBack,
  onConfirmAgreement,
  onSubmit,
}: Options): AuthorizeSkinController => {
  const [agreementDialogVisible, setAgreementDialogVisible] = useState(false)
  const [retentionVisible, setRetentionVisible] = useState(false)
  const [retentionCanClose, setRetentionCanClose] = useState(false)
  const [productCountdownStarted, setProductCountdownStarted] = useState(false)
  const [remainingProductCountdownTenths, setRemainingProductCountdownTenths] = useState(0)

  const timedRetentionShownRef = useRef(false)
  const exitRetentionShownRef = useRef(false)
  const productCountdownStartedRef = useRef(false)

  const triggerRetention = useCallback((byExit: boolean) => {
    const shownRef = byExit ? exitRetentionShownRef : timedRetentionShownRef
    if (shownRef.current) {
      return false
    }
    shownRef.current = true
    // 超时挽留与返回挽留各有一次机会，第二次弹出时不能沿用第一次的关闭状态。
    setRetentionCanClose(false)
    setRetentionVisible(true)
    window.setTimeout(() => {
      setRetentionCanClose(true)
    }, RETENTION_CLOSE_DELAY)
    return true
  }, [])

  // 停留超过指定时长后自动弹出挽留弹窗（整个生命周期只展示一次）
  useEffect(() => {
    const timer = window.setTimeout(() => {
      triggerRetention(false)
    }, RETENTION_TIMER_DELAY)

    return () => window.clearTimeout(timer)
  }, [triggerRetention])

  useEffect(() => {
    if (model.submitDisabled || productCountdownStartedRef.current) {
      return undefined
    }

    // 旧容器的短倒计时结束后，才展示与挽留弹窗一致的额度失效倒计时。
    productCountdownStartedRef.current = true
    setProductCountdownStarted(true)
    setRemainingProductCountdownTenths(PRODUCT_COUNTDOWN_INITIAL_TENTHS)
    const timer = window.setInterval(() => {
      setRemainingProductCountdownTenths(current => {
        if (current <= 1) {
          window.clearInterval(timer)
          return 0
        }
        return current - 1
      })
    }, PRODUCT_COUNTDOWN_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [model.submitDisabled])

  const handleBack = useCallback(() => {
    if (triggerRetention(true)) {
      return
    }
    onBack()
  }, [onBack, triggerRetention])

  const handleSubmit = useCallback(() => {
    if (!model.hasSelectedProducts || model.agreementChecked) {
      onSubmit()
      return
    }
    setAgreementDialogVisible(true)
  }, [model.agreementChecked, model.hasSelectedProducts, onSubmit])

  const handleSubmitFromRetention = useCallback(() => {
    setRetentionVisible(false)
    // 挽留弹窗已明确告知“立即提交”视为同意协议，直接复用旧容器的确认协议提交链路。
    onConfirmAgreement()
  }, [onConfirmAgreement])

  const handleConfirmAgreement = useCallback(() => {
    setAgreementDialogVisible(false)
    onConfirmAgreement()
  }, [onConfirmAgreement])

  const productCountdownText = productCountdownStarted
    ? formatProductCountdownText(remainingProductCountdownTenths)
    : ''

  return {
    agreementDialogVisible,
    retentionVisible,
    retentionCanClose,
    submitLabel: productCountdownText
      ? `立即领取 ${productCountdownText}`
      : model.submitLabel,
    productCountdownText,
    onBack: handleBack,
    onSubmit: handleSubmit,
    onConfirmAgreement: handleConfirmAgreement,
    onCloseAgreementDialog: () => setAgreementDialogVisible(false),
    onCloseRetention: () => setRetentionVisible(false),
    onSubmitFromRetention: handleSubmitFromRetention,
  }
}

export default useAuthorizeSkinController
